/// FOURJS_START_COPYRIGHT(D,2021)
/// Property of Four Js*
/// (c) Copyright Four Js 2021, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

//Required by ESlint not to raise errors

"use strict";

modulum("VMSessionNavigationManager", ["EventListener"], function (context, cls) {
  /**
   * Manager responsible for the navigation
   * It's responsible for registering applications and windows
   * and link them in order to retrieve them easily
   * from anywhere in the GBC
   * @class VMSessionNavigationManager
   * @memberOf classes
   * @extends classes.EventListener
   * @publicdoc Base
   */
  cls.VMSessionNavigationManager = context.oo.Class(cls.EventListener, function ($super) {
    return /** @lends classes.VMSessionNavigationManager.prototype */ {
      __name: "VMSessionNavigationManager",
      /** @type {classes.VMSession} */
      _session: null,

      /** @type {classes.VMApplication} */
      _currentApplication: null,

      /** @type {Map<string, classes.VMApplication>} */
      _applicationLookupByProcId: null,

      /** @type {Map<string, string>} */
      _applicationParentLookup: null,

      /** @type {WeakMap<classes.VMApplication, string>} */
      _applicationProcIds: null,

      /** @type {Map<string, Object>} */
      _lastActiveWindow: null,

      /**
       * A map where keys are application procId,
       * and values are windows belonging to the application
       *
       * *Windows are grouped by root application*
       * @type {Map<string, classes.WindowNode[]>}
       */
      _windowStacks: null,

      /**
       * @inheritDoc
       * @constructs
       * @param {classes.VMSession} session session
       */
      constructor: function (session) {
        $super.constructor.call(this);
        this._session = session;

        this._applicationLookupByProcId = new Map();
        this._applicationParentLookup = new Map();

        this._applicationProcIds = new WeakMap();
        this._lastActiveWindow = new Map();

        this._windowStacks = new Map();
      },

      /**
       * @override
       */
      destroy: function () {
        this._applicationLookupByProcId = null;
        this._applicationParentLookup = null;

        this._session = null;
        this._lastActiveWindow.clear();
        this._lastActiveWindow = null;

        $super.destroy.call(this);
      },

      /**
       * Get the owning session
       * @returns {classes.VMSession}
       */
      getSession: function () {
        return this._session;
      },

      //#region APPLICATION

      /**
       * Adding an app in Manager
       * @param {classes.VMApplication} application
       * @returns {?classes.WidgetBase}
       */
      addApplication: function (application) {
        gbc.HostService.updateDisplay();
        this.emit(context.constants.baseEvents.applicationAdded, application);
      },

      /**
       * Update the application information in the registry
       * @param {VMApplication} application
       */
      updateApplicationInformation: function (application) {
        const appInfo = application.applicationInfo.connectionInfo;
        let procId = null,
          procIdParent = null;
        if (appInfo) {
          procId = appInfo.procId;
          procIdParent = appInfo.procIdParent || null;
          this._applicationLookupByProcId.set(procId, application);
          this._applicationParentLookup.set(procId, procIdParent);
          this._applicationProcIds.set(application, procId);
        }
      },

      removeApplication: function (application) {
        if (this._applicationProcIds.has(application)) {
          const procIdToDelete = this._applicationProcIds.get(application);
          this._applicationProcIds.delete(application);
          this._applicationLookupByProcId.delete(procIdToDelete);
        }
        this.emit(context.constants.baseEvents.applicationRemoved, application);
      },

      /**
       * Returns the last awaited application of the chain.
       * From application given in parameter to the last application of the chain
       * @param {classes.VMApplication} parentApplication
       * @returns {classes.VMApplication|null} The awaited application. `null` if no application is awaited
       */
      getAwaitedApplication(parentApplication) {
        let parentProcId = parentApplication.getProcId();

        let awaitedApp = null;
        // For all application recursively check the waiting chain
        let mightBeWaiting = false;
        do {
          mightBeWaiting = false;
          for (const app of this._applicationLookupByProcId.values()) {
            if (app.getProcIdWaiting() === parentProcId) {
              awaitedApp = app;
              parentProcId = app.getProcId();
              mightBeWaiting = true;
              break;
            }
          }
        } while (mightBeWaiting);

        return awaitedApp;
      },

      /**
       * Get the application by is procId
       * @param procId
       * @return {classes.VMApplication}
       */
      getApplicationByProcId: function (procId) {
        return this._applicationLookupByProcId.get(procId);
      },

      /**
       * Get the root waiting application for application given in parameter
       * @param {classes.VMApplication} application
       * @return {classes.VMApplication|null} Return null if the app is awaited but have no appInfo
       */
      getRootWaitingApplication: function (application) {
        let app = application;
        let procId = app.getProcId();

        do {
          if (!app.getProcIdWaiting() || !app.getProcIdParent()) {
            return app;
          }

          procId = app.getProcIdParent();
          app = this._applicationLookupByProcId.get(procId);
        } while (app);

        // Reaching this point mean that the application
        // is awaited by an unregistered application
        return null;
      },

      /**
       * Returns an array with the waiting applications hierarchy
       * First item is the root application and after all the child applications
       * Each application has its parent (parentProcId) which is the previous element in the array
       * This is used for applications started with a FGL RUN (with waiting), an application can have only one child app
       * @param {classes.VMApplication} application
       * @returns {classes.VMApplication[]}
       */
      getWaitingApplicationsHierarchy(application) {
        const apps = [];

        let app = this.getRootWaitingApplication(application);
        while (app) {
          apps.push(app);
          app = this.getWaitForApp(app);
        }
        return apps;
      },

      /**
       * Returns the application on which parentApp is waiting for, undefined if no application
       * @param {classes.VMApplication} parentApp
       * @returns {classes.VMApplication}
       */
      getWaitForApp(parentApp) {
        const currentSession = context.SessionService.getCurrent();
        return /** @type classes.VMApplication */ currentSession.getApplications().find((app) => app.getProcIdWaiting() === parentApp.getProcId());
      },

      /**
       * Return a new array composed of all windows from all waiting applications hierarchy
       * @param {null|classes.VMApplication} application
       * @return {classes.NodeBase[]}
       */
      getWaitingAppWindows(application) {
        const currentSession = context.SessionService.getCurrent();
        const currentApp = application || currentSession.getCurrentApplication();
        const waitingApps = currentSession.getNavigationManager().getWaitingApplicationsHierarchy(currentApp);
        return waitingApps.flatMap((app) => app.uiNode()?.getChildren("Window") ?? []);
      },

      /**
       * Get parent application
       * @param {classes.VMApplication} application
       * @return {null|classes.VMApplication}
       */
      getParentApplication: function (application) {
        const parentProcId = application.getProcIdParent() || application.applicationInfo.taskProcIdParent;

        if (parentProcId) {
          return this._applicationLookupByProcId.get(parentProcId);
        }

        return null;
      },

      /**
       * Get the last added child application
       * @param {classes.VMApplication} application The parent application
       * @return {null|classes.VMApplication}
       */
      getChildApplication: function (application) {
        let appProcId = application.getProcId();

        let childApp = null;
        // take last child based on insertion order
        for (const app of this._applicationLookupByProcId.values()) {
          if (app.getProcIdParent() === appProcId) {
            childApp = app;
          }
        }

        return childApp;
      },

      /**
       * Checks if the application is waiting for a child application to close
       * @param {classes.VMApplication} application
       * @returns {boolean}
       */
      isApplicationWaiting: function (application) {
        return application.getProcId() === this.getChildApplication(application)?.applicationInfo.connectionInfo.procIdWaiting;
      },

      /**
       * Get all the applications
       * @return {classes.VMApplication[]}
       */
      getApplications: function () {
        const res = [];

        this._applicationLookupByProcId.forEach((value) => {
          res.push(value);
        });

        return res;
      },

      /**
       * Event triggered when an application is added to the Session
       * @param {Function} hook Method triggered when the event is emitted. The hook should have the following parameter : (evt, invoker, application)
       * @returns {HandleRegistration} a registration handle (for unbind purpose)
       */
      onApplicationAdded: function (hook, once) {
        return this.when(context.constants.baseEvents.applicationAdded, hook);
      },
      /**
       * Event triggered when an application is removed of the Session
       * @param {Function} hook Method triggered when the event is emitted. The hook should have the following parameter : (evt, invoker, application)
       * @param {Boolean} once True to automatically unbind the handler
       * @returns {HandleRegistration} a registration handle (for unbind purpose)
       */
      onApplicationRemoved: function (hook, once) {
        return this.when(context.constants.baseEvents.applicationRemoved, hook, once);
      },
      /**
       * Register a handler called when current application change
       * The event parameters are :
       * 1. classes.VMApplication : current application
       * 2. classes.VMApplication : previous current application
       * @param {Function} hook Hook to call
       * @returns {HandleRegistration}
       */
      onApplicationSwitched: function (hook) {
        return this.when(context.constants.baseEvents.switchCurrentApplication, hook);
      },

      displayApplication: function (application) {
        const previousApplication = this.getSession().getCurrentApplication();
        this.getSession().setCurrentApplication(application);
        this.emit(context.constants.baseEvents.switchCurrentApplication, application, previousApplication);
      },
      //#endregion

      //#region WINDOW

      /**
       * Add a window in the registry
       * and emit an event to notify that a window was added
       * This method should be called once the window is attached to the UI
       * @param {cls.WindowNode} windowNode The window to register
       */
      registerWindow: function (windowNode) {
        // Get the root application of the controller
        const rootApplication = this.getRootWaitingApplication(windowNode.getApplication()),
          rootProcId = rootApplication?.getProcId();
        if (!this._windowStacks.has(rootProcId)) {
          this._windowStacks.set(rootProcId, []);
        }
        const windowList = this._windowStacks.get(rootProcId);
        if (windowList.findIndex((node) => node === windowNode) !== -1) {
          // The window was already registered. Just quit
          return;
        }
        windowList.push(windowNode);
        // Add this window to the window stack if application list is not top
        if (!context.ApplicationListService.isApplicationListInChromeBar()) {
          context.WindowListService.register(windowNode.getWidget());
        }
        this.emit(context.constants.applicationEvents.windowAdded, windowNode, rootApplication);
      },

      /**
       * Remove the registry entry for the window given in parameter
       * if any.
       * And notify that a window was removed
       * This method should be called just before the window get removed from the UI
       * @param {cls.WindowNode} windowNode The window to remove from registry
       */
      unregisterWindow: function (windowNode) {
        const rootApplication = this.getRootWaitingApplication(windowNode.getApplication()),
          windowList = this._windowStacks.get(rootApplication?.getProcId()),
          windowIndex = windowList.findIndex((node) => node === windowNode);
        if (windowIndex === -1) {
          // This window node wasn't registered
          return;
        }
        // Remove the node from the list
        windowList.splice(windowIndex, 1);
        if (!context.ApplicationListService.isApplicationListInChromeBar()) {
          context.WindowListService.unregister(windowNode.getWidget());
        }
        this.emit(context.constants.applicationEvents.windowRemoved, windowNode, rootApplication);
      },

      /**
       * Save the last VMApplication and active window id
       * @param {classes.VMApplication} application
       * @param {number} lastActiveWindowId
       */
      setLastActiveWindow: function (application, lastActiveWindowId) {
        let rootApp = this.getRootWaitingApplication(application);

        if (!rootApp) {
          return;
        }

        this._lastActiveWindow.set(rootApp.getProcId(), {
          ui: application.getUI(),
          windowId: lastActiveWindowId
        });
      },

      /**
       * Go back to the last active window id
       * @param {classes.VMApplication|undefined} application If undefined, go back to the last active window of the current application
       */
      goBackToLastActiveWindow: function (application) {
        const procId = this.getRootWaitingApplication(application || this.getSession().getCurrentApplication()).getProcId(),
          info = this._lastActiveWindow.get(procId);

        info.ui.syncCurrentWindow();
        info.ui.setCurrentWindow(info.windowId);

        const uiWidget = info.ui.getWidget();
        uiWidget.domAttributesMutator(() => uiWidget.removeClass("inactiveWindow"));
      },

      /**
       * Store the windowNode as current window
       * and force the title to synchronise
       * @param {classes.WindowNode} windowNode
       */
      setCurrentWindow: function (windowNode) {
        const previousWindow = this._currentWindow;
        const app = windowNode.getApplication();
        this._currentApplication = app;
        this._currentWindow = windowNode;
        if (windowNode.getTitle()) {
          windowNode.setTitle(windowNode.getTitle()); // force title sync if any
        }
        this.emit(context.constants.applicationEvents.currentWindowChanged, this._currentWindow, previousWindow);
      },

      getCurrentWindow: function () {
        return this._currentWindow;
      },

      /**
       * Query the root application of the one given in parameter
       * Then return all the linked windows of this root application
       * @param {VMApplication} application The reference application to get linked windows from
       * @returns {WindowNode[]} An array containing the window nodes bound to the root application.
       */
      getWindows: function (application) {
        const rootApplication = this.getRootWaitingApplication(application),
          procId = rootApplication?.getProcId();

        if (this._windowStacks.has(procId)) {
          return this._windowStacks.get(rootApplication.getProcId());
        }
        return [];
      },

      /**
       * Switch application if needed to display the correct window
       * @param {cls.WindowNode} windowNode Display the correct window
       */
      displayWindow(windowNode) {
        const ui = windowNode.getApplication().getUI();
        // Display the application
        ui.syncCurrentWindow();
        // Display the good window
        ui.setCurrentWindow(windowNode.getId());
      }

      //#endregion
    };
  });
});
